import { loadProgress, saveProgress } from '../shared/progress';
import { TOTAL_BOSS_COUNT } from '../game/progression/boss-weapon-unlocks';
import { renderMyProgressCard } from './my-progress-card';
import { renderDayOneBanner } from './dayone-banner';
import { snapshotHook } from './dayone-hook';
import { processStreakOnHomeEnter } from './streak/streak';
import { renderStreakBanner } from './streak/streak-ui';
import { renderStreakPill } from './components/StreakPill';
import {
  renderFirstSkillModal,
  shouldShowFirstSkillModal,
  dispatchFirstSkillUnlock,
  FACTION_FIRST_SKILL_ID,
  FIRST_SKILL_MODAL_DISMISSED_KEY,
} from './components/FirstSkillModal';
import { checkAndUnlockBadges } from './badges/badges';
import { renderBadgeRevealBanner } from './badges/badges-ui';
import { processSeasonOnHomeEnter } from './season-pass/season-pass';
import { renderSeasonPassBanner, renderSeasonResetModal } from './season-pass/season-pass-ui';
import { installSeasonPassHooks } from './season-pass/season-pass-hooks';
import {
  autoEvolveAll,
  distinctCaughtCount,
  MONSTER_CATALOGUE,
} from './monsters/monsters';
import {
  openMonsterDexModal,
  renderMonsterRevealBanner,
} from './monsters/monsters-ui';
import { installMonsterHooks } from './monsters/monsters-hooks';
import { pickBannerSlots } from './home-banner-queue';
import {
  renderNextDayPreviewCard,
  clearSessionPending,
} from './components/NextDayPreviewCard';

/**
 * Phase 2 「實驗中」 cards that MVP_MODE hides from kids by default.
 * Adding to this list = also gate render + click handler below.
 * See task #49 / docs/PHASE1_MVP_READINESS.md §5.
 */
export const PHASE2_CARD_IDS = ['card-campaign', 'card-tower', 'card-tower-leaderboard'] as const;

export function isMvpMode(progress: ReturnType<typeof loadProgress>): boolean {
  // experimentalMode !== true → MVP on. undefined (fresh user) defaults to MVP on.
  return progress?.parentSettings?.experimentalMode !== true;
}

export interface HomeActions {
  onBattle: () => void;
  onTrainingHall: () => void;
  onCampaign?: () => void;
  onTower?: () => void;
  onTowerLeaderboard?: () => void;
  onBossGallery?: () => void;
  onDailyChallenge?: () => void;
  onHBattle?: () => void;
  onParentDashboard?: () => void;
  /**
   * ← 回學習堡壘 — 遊戲首頁回到 learn-portal 4 門(學/練/複習+拍考卷)。
   * LEARN_IA_CONSOLIDATION:遊戲首頁(home)只剩遊戲卡,學習捷徑全收進 learn-portal。
   * 若未提供 callback,card 會 dispatch `ef:return-home`(main.ts: setMode('learn-portal'))。
   */
  onBackToPortal?: () => void;
}

export function renderHome(container: HTMLElement, actions: HomeActions): void {
  const progress = loadProgress();
  const root = document.createElement('div');
  root.className = 'home';

  // Cog icon — fixed top-right, opens a parent menu (kid never sees the dashboard option label)
  const cog = document.createElement('button');
  cog.className = 'cog-icon';
  cog.textContent = '⚙️';
  cog.style.cssText = 'position: fixed; top: 16px; right: 16px; background: transparent; border: none; font-size: 28px; cursor: pointer; z-index: 100;';
  cog.addEventListener('click', () => {
    openCogMenu(cog, root);
  });
  container.appendChild(cog);

  const freeXP = progress?.freeXP ?? 0;
  // task #49: MVP_MODE hides 3 Phase 2 game cards (戰役 / 無限魔王塔 / 塔記錄板).
  // Default ON (per docs/PHASE1_MVP_READINESS.md §5). Toggle via settings 🧪 體驗模式.
  const mvpMode = isMvpMode(progress);
  // 🧒 小明 2026-05-21 01:13 leftover fix: surface boss gallery so kids can see
  // how many bosses they've beaten (motivation / dopamine hit). Counter shown
  // in the card sub-text so even an unbeaten 0/N is informative.
  const bossesDefeatedCount = (progress?.bossesDefeated ?? []).length;
  // C03 monster collection counter — shown in the "怪獸圖鑑" card sub-text
  // for at-a-glance "我抓了幾隻 / 全部 N 隻" progress feedback. Uses the
  // distinctCaughtCount helper (excludes catalogue orphans).
  const monstersCaughtCount = progress ? distinctCaughtCount(progress) : 0;

  root.innerHTML = `
    <div class="home-header-row" style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
      <h1 style="margin:0;">${progress ? `${escapeHtml(progress.studentName)},指揮官!` : '永恆要塞'}</h1>
      <div id="streak-pill-slot" style="display:flex;align-items:center;"></div>
    </div>
    <div id="dayone-banner-slot"></div>
    <div id="streak-banner-slot"></div>
    <div id="badge-reveal-slot"></div>
    <div id="season-pass-slot"></div>
    <div id="monster-reveal-slot"></div>
    <div id="next-day-preview-slot"></div>
    <div id="progress-card-slot"></div>

    <!-- ── ← 回學習堡壘 — 遊戲首頁回到 learn-portal 4 門(學/練/複習+拍考卷)。
         LEARN_IA_CONSOLIDATION:遊戲首頁只剩遊戲卡,學習捷徑全收進學習堡壘,避免兩個首頁
         重複讓家長迷路。放最上方,確保一進遊戲首頁就看得到返回路徑。 -->
    <button class="secondary-btn" id="card-back-portal"
            style="display:flex;align-items:center;gap:6px;background:transparent;border:none;color:var(--ef-ink-muted,#5a6472);font-size:14px;cursor:pointer;padding:6px 2px;margin-bottom:4px;">
      ‹ 回學習堡壘
    </button>

    <!-- ── Hero CTA row: 進車庫 large (遊戲端唯一主入口 → 科技魔幻車庫 GarageScene)。
         boss 2026-06-15「車庫在哪」:此卡實際開的是「我的車庫」,文案從「出戰」改成「車庫」
         才找得到;出戰/闖關在車庫內按。 -->
    <div class="home-hero-row">
      <div class="action-card hero-battle" id="card-battle">
        <div class="icon">🏠</div>
        <div class="label">我的車庫</div>
        <div class="sub">看戰車 · 升級基地 · 出戰</div>
      </div>
    </div>

    <!-- garage-consolidation (boss 2026-06-15):原「選擇主角 / 選擇武器」兩張卡已移除 —
         與科技魔幻車庫 GarageScene 重複。選戰車 + 選武器一律進「我的車庫」一個地方做
         (車庫內點戰車有門派玩法面板、點武器架換主武器)。避免兩處平行入口讓孩子迷路。 -->

    <!-- ── 更多 toggle ── -->
    <button class="secondary-btn more-toggle" id="more-toggle">更多 ▼</button>
    <div class="home-more-row" id="home-more-row" style="display:none;">
      <div class="action-card action-card-sm" id="card-hall">
        <div class="icon">🏛️</div>
        <div class="label">修煉殿</div>
        <div class="sub">自由修煉值: <strong>${freeXP}</strong></div>
      </div>
      ${mvpMode ? '' : `
      <div class="action-card action-card-sm" id="card-campaign">
        <div class="icon">🗺️</div>
        <div class="label">戰役</div>
        <div class="sub">節點圖</div>
      </div>
      <div class="action-card action-card-sm" id="card-tower">
        <div class="icon">🗼</div>
        <div class="label">無限魔王塔</div>
        <div class="sub">爬塔挑戰</div>
      </div>
      <div class="action-card action-card-sm" id="card-tower-leaderboard">
        <div class="icon">🏆</div>
        <div class="label">塔記錄板</div>
        <div class="sub">本機 Top 10</div>
      </div>`}
      <div class="action-card action-card-sm" id="card-boss-gallery">
        <div class="icon">👾</div>
        <div class="label">魔王圖鑑</div>
        <div class="sub">已打敗 <strong>${bossesDefeatedCount}</strong> / ${TOTAL_BOSS_COUNT}</div>
      </div>
      <div class="action-card action-card-sm" id="card-monster-dex">
        <div class="icon">📔</div>
        <div class="label">怪獸圖鑑</div>
        <div class="sub">已收集 <strong>${monstersCaughtCount}</strong> / ${MONSTER_CATALOGUE.length}</div>
      </div>
      <div class="action-card action-card-sm parent-card" id="card-parent-dashboard">
        <div class="icon">👪</div>
        <div class="label">家長中心</div>
        <div class="sub">週報 / 時間管控</div>
      </div>
    </div>
  `;
  container.appendChild(root);

  // ── QA #263 fix #1: banner priority queue ─────────────────────────────────
  // PRE-PROCESS state-mutating hooks first (always, regardless of which banner
  // surfaces): streak tick, badge evaluation, season tick, monster auto-evolve.
  // Then pick at most 2 banners to render per the priority queue — a 9-year-old
  // cannot triage 5 stacked CTAs on first impression. Fresh kids get ONLY the
  // dayone banner; everything else stays queued until next home render.
  //
  // Important: banners NOT picked this render skip mount entirely — their
  // one-shot markers (lastUnlocked / lastNew) stay in progress so the next
  // home render can surface them.
  let bannerSlotsToShow: ReadonlyArray<string> = [];
  if (progress) {
    // 1. Run all state mutations (these are independent of which banner shows).
    try { checkAndUnlockBadges(progress); } catch (err) { console.warn('[home] badge eval failed:', err); }
    try { autoEvolveAll(progress); } catch (err) { console.warn('[home] monster auto-evolve failed:', err); }
    // streak/season ticks happen inside the per-banner mount blocks below so
    // the snapshot they return matches the just-mutated state.

    // 2. Pick which banners to surface this render (max 2).
    try {
      bannerSlotsToShow = pickBannerSlots(progress);
    } catch (err) {
      console.warn('[home] banner queue pick failed (non-fatal):', err);
      bannerSlotsToShow = [];
    }
  }

  // Always install global hooks (idempotent — safe to call every render).
  installSeasonPassHooks();
  installMonsterHooks();

  const wantDayone = bannerSlotsToShow.includes('dayone');
  const wantBadge = bannerSlotsToShow.includes('badge');
  const wantMonster = bannerSlotsToShow.includes('monster');
  const wantStreak = bannerSlotsToShow.includes('streak');
  const wantSeason = bannerSlotsToShow.includes('season');
  const wantNextDayPreview = bannerSlotsToShow.includes('next-day-preview');

  // dayone banner — always seeds firstLaunchAt; we still call it to seed when
  // chosen. When NOT chosen we skip mount but seeding has already happened on
  // a prior render (or will on the next).
  const bannerSlot = root.querySelector('#dayone-banner-slot') as HTMLElement;
  if (bannerSlot && progress && wantDayone) {
    try {
      renderDayOneBanner(bannerSlot, progress);
    } catch (err) {
      console.warn('[home] dayone banner mount failed (non-fatal):', err);
    }
  }

  // streak banner — tick always (idempotent within UTC day) so streak state
  // stays current even when the banner isn't surfaced this render.
  // Also mount the permanent header pill (Day-1 retention QW #2) from the
  // same snapshot — pill is NOT part of the banner priority queue; it lives
  // in the header row so 「我連續了 X 天」 is always visible.
  const streakSlot = root.querySelector('#streak-banner-slot') as HTMLElement;
  const pillSlot = root.querySelector('#streak-pill-slot') as HTMLElement;
  if (streakSlot && progress) {
    try {
      const snapshot = processStreakOnHomeEnter(progress);
      if (wantStreak) renderStreakBanner(streakSlot, { snapshot });
      if (pillSlot) renderStreakPill(pillSlot, snapshot.days);
    } catch (err) {
      console.warn('[home] streak banner mount failed (non-fatal):', err);
    }
  } else if (pillSlot) {
    // Fresh / no-progress edge: still mount the placeholder pill so the
    // header chip is visually consistent (kid sees「開始今天的第 1 天 →」).
    try { renderStreakPill(pillSlot, 0); } catch { /* noop */ }
  }

  // badge banner — only renderBadgeRevealBanner CONSUMES lastUnlocked, so
  // skipping mount keeps the marker for next home render.
  const badgeSlot = root.querySelector('#badge-reveal-slot') as HTMLElement;
  if (badgeSlot && progress && wantBadge) {
    try {
      renderBadgeRevealBanner(badgeSlot, progress);
    } catch (err) {
      console.warn('[home] badge reveal mount failed (non-fatal):', err);
    }
  }

  // season banner — tick always so reset modal still fires when it should,
  // but only mount the banner UI when surfaced.
  const seasonSlot = root.querySelector('#season-pass-slot') as HTMLElement;
  if (seasonSlot && progress) {
    try {
      const result = processSeasonOnHomeEnter(progress);
      if (result.resetInfo) {
        // Season just rolled over — show the「上季結算」modal once. The
        // progress object has already been mutated by tickSeason(); the
        // snapshot reflects the NEW season.
        renderSeasonResetModal(
          document.body,
          result.resetInfo,
          progress.seasonPass!.currentSeason,
        );
      }
      if (wantSeason) {
        renderSeasonPassBanner(seasonSlot, {
          snapshot: result.snapshot,
          progress,
        });
      }
    } catch (err) {
      console.warn('[home] season-pass banner mount failed (non-fatal):', err);
    }
  }

  // monster reveal banner — only renderMonsterRevealBanner CONSUMES lastNew,
  // so skipping mount keeps the marker for next home render.
  const monsterSlot = root.querySelector('#monster-reveal-slot') as HTMLElement;
  if (monsterSlot && progress && wantMonster) {
    try {
      renderMonsterRevealBanner(monsterSlot, progress);
    } catch (err) {
      console.warn('[home] monster reveal mount failed (non-fatal):', err);
    }
  }

  // next-day-preview banner (Day-1 retention QW #3) — surfaced when the kid
  // just finished a quiz session AND we haven't shown the preview today. The
  // gate is set inside renderNextDayPreviewCard; clear the session-end flag
  // here so a second home render today doesn't re-surface even if the gate
  // ISO somehow gets cleared.
  const ndpSlot = root.querySelector('#next-day-preview-slot') as HTMLElement;
  if (ndpSlot && wantNextDayPreview) {
    try {
      renderNextDayPreviewCard(ndpSlot, progress ?? null, () => { /* noop dismiss */ });
      clearSessionPending();
    } catch (err) {
      console.warn('[home] next-day-preview mount failed (non-fatal):', err);
    }
  }

  // Mount 我的成長 card before the action cards
  const slot = root.querySelector('#progress-card-slot') as HTMLElement;
  if (slot) renderMyProgressCard(slot);

  // 更多 toggle
  const moreToggleBtn = root.querySelector('#more-toggle') as HTMLButtonElement;
  const moreRow = root.querySelector('#home-more-row') as HTMLElement;
  moreToggleBtn.addEventListener('click', () => {
    const expanded = moreRow.style.display !== 'none';
    moreRow.style.display = expanded ? 'none' : 'flex';
    moreToggleBtn.textContent = expanded ? '更多 ▼' : '收起 ▲';
  });

  // ← 回學習堡壘 — LEARN_IA_CONSOLIDATION:遊戲首頁回到 learn-portal 4 門。
  // callback 優先,否則 dispatch ef:return-home (main.ts: setMode('learn-portal'))。
  root.querySelector('#card-back-portal')!.addEventListener('click', () => {
    root.remove(); cog.remove();
    if (actions.onBackToPortal) actions.onBackToPortal();
    else window.dispatchEvent(new CustomEvent('ef:return-home'));
  });
  root.querySelector('#card-battle')!.addEventListener('click', () => { root.remove(); cog.remove(); if (actions.onHBattle) actions.onHBattle(); else actions.onBattle(); });
  // garage-consolidation (boss 2026-06-15):「選擇主角 / 選擇武器」兩張卡已移除 —
  // 改在「我的車庫」(GarageScene)內選戰車 + 選武器,首頁不再有平行入口。
  root.querySelector('#card-hall')!.addEventListener('click', () => { root.remove(); cog.remove(); actions.onTrainingHall(); });
  if (!mvpMode) {
    root.querySelector('#card-campaign')!.addEventListener('click', () => {
      root.remove(); cog.remove();
      if (actions.onCampaign) actions.onCampaign();
    });
    root.querySelector('#card-tower')!.addEventListener('click', () => {
      root.remove(); cog.remove();
      if (actions.onTower) actions.onTower();
    });
    root.querySelector('#card-tower-leaderboard')!.addEventListener('click', () => {
      root.remove(); cog.remove();
      if (actions.onTowerLeaderboard) actions.onTowerLeaderboard();
    });
  }
  root.querySelector('#card-boss-gallery')!.addEventListener('click', () => {
    root.remove(); cog.remove();
    if (actions.onBossGallery) actions.onBossGallery();
    else window.dispatchEvent(new CustomEvent('ef:open-boss-gallery'));
  });
  // C03 monster dex — opens an overlay modal IN PLACE (no route change), so
  // we don't tear down the home root. Modal handles its own dismissal.
  root.querySelector('#card-monster-dex')!.addEventListener('click', () => {
    if (progress) openMonsterDexModal(progress);
  });
  root.querySelector('#card-parent-dashboard')!.addEventListener('click', () => {
    root.remove(); cog.remove();
    if (actions.onParentDashboard) actions.onParentDashboard();
    else window.dispatchEvent(new CustomEvent('ef:open-dashboard'));
  });

  // ── Day-1 Retention QW #1: FirstSkillModal trigger ────────────────────────
  // 條件:progress 存在 & unlockedSkills 為空 & 24hr 內未被 dismiss。
  // 不影響任何戰鬥/答題流程 — 純 home 層彩光 retention banner。
  if (progress) {
    try {
      maybeShowFirstSkillModal(progress);
    } catch (err) {
      console.warn('[home] first-skill modal trigger failed (non-fatal):', err);
    }
  }
}

/**
 * Day-1 Retention QW #1 trigger — 內部 helper,從 renderHome 結尾呼叫。
 * 抽出來方便測試 & 隔離 try/catch。
 */
function maybeShowFirstSkillModal(progress: ReturnType<typeof loadProgress>): void {
  if (!progress) return;
  // De-clutter fix (boss「上方擠成一團」): on first launch the 新手禮包 welcome
  // overlay is the single onboarding moment. Don't ALSO stack the「送你第一個技能」
  // modal on top of it — claiming the welcome gift unlocks 擴散三連發, after which
  // this modal naturally stops qualifying. Defer until the gift is dealt with.
  try {
    if (snapshotHook(progress).showGiftOverlay) return;
  } catch { /* snapshot failed — fall through to normal gate */ }
  const len = (progress.unlockedSkills ?? []).length;
  const dismissedISO = localStorage.getItem(FIRST_SKILL_MODAL_DISMISSED_KEY);
  if (!shouldShowFirstSkillModal(len, dismissedISO)) return;

  renderFirstSkillModal(
    document.body,
    (faction) => {
      // 解鎖 — 寫 progress + dispatch event + toast
      const skillId = FACTION_FIRST_SKILL_ID[faction];
      if (skillId && !progress.unlockedSkills.includes(skillId)) {
        progress.unlockedSkills.push(skillId);
      }
      saveProgress(progress);
      dispatchFirstSkillUnlock(faction);
      // 清掉 dismiss 標記 — 已成功解鎖,後續邏輯不該再 gate 在這個 key
      try { localStorage.removeItem(FIRST_SKILL_MODAL_DISMISSED_KEY); } catch { /* noop */ }
      showFirstSkillToast('解鎖成功!到 academy 試試新技能');
    },
    () => {
      // 跳過 — 寫 dismiss timestamp,24hr 內不再彈
      try {
        localStorage.setItem(FIRST_SKILL_MODAL_DISMISSED_KEY, new Date().toISOString());
      } catch { /* localStorage 滿了 — 不阻斷 UX */ }
    },
  );
}

/**
 * Lightweight inline toast — 4 秒自動消失。
 * 不引入外部 toast helper 避免循環依賴 (settings.ts 也有自己的版本,後續可統一)。
 */
function showFirstSkillToast(message: string): void {
  const toast = document.createElement('div');
  toast.setAttribute('data-testid', 'first-skill-toast');
  toast.textContent = message;
  toast.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:32px',
    'transform:translateX(-50%)',
    'background:rgba(33,33,33,0.92)',
    'color:#FFFFFF',
    'padding:12px 18px',
    'border-radius:24px',
    'font-size:14px',
    'font-weight:600',
    'box-shadow:0 4px 16px rgba(0,0,0,0.35)',
    'z-index:10000',
    'pointer-events:none',
    'max-width:90vw',
  ].join(';');
  document.body.appendChild(toast);
  setTimeout(() => { toast.remove(); }, 4000);
}

function openCogMenu(anchor: HTMLElement, homeRoot: HTMLElement): void {
  // Remove any existing menu
  document.querySelector('.cog-menu')?.remove();

  const menu = document.createElement('div');
  menu.className = 'cog-menu';
  menu.innerHTML = `
    <button id="cog-dashboard">家長報表</button>
    <button id="cog-settings">⚙️ 設定</button>
    <button id="cog-cancel">取消</button>
  `;

  menu.querySelector('#cog-dashboard')!.addEventListener('click', () => {
    menu.remove();
    homeRoot.remove();
    anchor.remove();
    window.dispatchEvent(new CustomEvent('ef:open-dashboard'));
  });
  menu.querySelector('#cog-settings')!.addEventListener('click', () => {
    menu.remove();
    homeRoot.remove();
    anchor.remove();
    window.dispatchEvent(new CustomEvent('ef:open-settings'));
  });
  menu.querySelector('#cog-cancel')!.addEventListener('click', () => {
    menu.remove();
  });

  // Close on outside click
  const closeOnOutside = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node) && e.target !== anchor) {
      menu.remove();
      document.removeEventListener('click', closeOnOutside, true);
    }
  };
  // Delay so the current click doesn't immediately close
  setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);

  document.body.appendChild(menu);
}

function escapeHtml(s: string): string {
  const d = document.createElement('div'); d.textContent = s; return d.innerHTML;
}
